import { useEffect, useState } from "react";
import api from "@/lib/api";
import { SectionCard } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Power } from "lucide-react";
import { toast } from "sonner";

export default function CategoryManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/categories");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setName("");
    setOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setName(c.name);
    setOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("Kategori adı boş olamaz");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/categories/${editing.id}`, { name: trimmed });
        toast.success("Kategori güncellendi");
      } else {
        await api.post("/categories", { name: trimmed });
        toast.success("Kategori eklendi");
      }
      setOpen(false);
      setEditing(null);
      setName("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "İşlem başarısız");
    } finally {
      setSaving(false);
    }
  };

  const onToggleStatus = async (c) => {
    const next = c.status === "active" ? "passive" : "active";
    try {
      await api.put(`/categories/${c.id}/status`, { status: next });
      toast.success(next === "passive" ? "Kategori pasife alındı" : "Kategori aktifleştirildi");
      load();
    } catch {
      toast.error("İşlem başarısız");
    }
  };

  const onDelete = async (c) => {
    const used = c.usage_count > 0;
    const msg = used
      ? `"${c.name}" kategorisi ${c.usage_count} kayıtta kullanılmış. Tamamen silinemez; Pasif yapılacak. Devam edilsin mi?`
      : `"${c.name}" kategorisi silinsin mi?`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.delete(`/categories/${c.id}`);
      if (data.action === "deactivated") {
        toast.success("Kategori pasife alındı (geçmiş kayıtlar korundu)");
      } else {
        toast.success("Kategori silindi");
      }
      load();
    } catch {
      toast.error("İşlem başarısız");
    }
  };

  const active = (items ?? []).filter((c) => c.status === "active");
  const passive = (items ?? []).filter((c) => c.status === "passive");

  return (
    <SectionCard
      title="Kategori Yönetimi"
      action={
        <Button
          size="sm"
          className="bg-slate-900 hover:bg-slate-800 rounded-lg"
          onClick={openCreate}
          data-testid="add-category-button"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Yeni Kategori
        </Button>
      }
    >
      <div className="p-5 space-y-4" data-testid="category-manager">
        <p className="text-xs text-slate-500">
          Bu kategoriler tedarikçi ve satın alma kayıtlarında kullanılır.
          Geçmişte kullanılmış kategoriler silinmez, otomatik olarak Pasif yapılır.
        </p>

        <div>
          <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
            Aktif ({(active ?? []).length})
          </div>
          <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
            {(active ?? []).length === 0 && (
              <li className="px-4 py-3 text-sm text-slate-400">Aktif kategori yok</li>
            )}
            {(active ?? []).map((c) => (
              <CategoryRow key={c.id} c={c} onEdit={openEdit} onToggle={onToggleStatus} onDelete={onDelete} />
            ))}
          </ul>
        </div>

        {(passive ?? []).length > 0 && (
          <div>
            <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
              Pasif ({(passive ?? []).length})
            </div>
            <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
              {(passive ?? []).map((c) => (
                <CategoryRow key={c.id} c={c} onEdit={openEdit} onToggle={onToggleStatus} onDelete={onDelete} passive />
              ))}
            </ul>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setName(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? "Kategori Düzenle" : "Yeni Kategori"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="category-form">
            <div className="space-y-1.5">
              <Label className="text-xs">Kategori Adı</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Örn: Eğitim, Pazarlama, Sigorta"
                autoFocus
                className="h-10 rounded-lg"
                data-testid="category-name-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Vazgeç
              </Button>
              <Button
                type="submit"
                className="bg-slate-900 hover:bg-slate-800"
                disabled={saving}
                data-testid="category-save-button"
              >
                {saving ? "Kaydediliyor..." : "Kaydet"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
}

function CategoryRow({ c, onEdit, onToggle, onDelete, passive }) {
  return (
    <li
      className={`flex items-center justify-between px-4 py-2.5 text-sm ${passive ? "bg-slate-50/40 text-slate-500" : "bg-white"}`}
      data-testid={`category-row-${c.id}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className="font-medium truncate">{c.name}</span>
        {c.is_default && (
          <span className="text-[10px] uppercase tracking-wider text-slate-400 border border-slate-200 rounded px-1.5 py-0.5">
            varsayılan
          </span>
        )}
        {c.usage_count > 0 && (
          <span className="text-[10px] text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">
            {c.usage_count} kayıt
          </span>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7"
          onClick={() => onEdit(c)}
          data-testid={`category-edit-${c.id}`}
          title="Düzenle"
        >
          <Pencil className="w-3.5 h-3.5" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className={`h-7 w-7 ${passive ? "text-emerald-600 hover:bg-emerald-50" : "text-amber-600 hover:bg-amber-50"}`}
          onClick={() => onToggle(c)}
          data-testid={`category-toggle-${c.id}`}
          title={passive ? "Aktifleştir" : "Pasife Al"}
        >
          <Power className="w-3.5 h-3.5" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-red-600 hover:bg-red-50"
          onClick={() => onDelete(c)}
          data-testid={`category-delete-${c.id}`}
          title="Sil"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </li>
  );
}
